import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { GetStaticPaths, GetStaticProps } from "next/types";
import styled from "styled-components";
import { Preview, ToolTip } from "components";
import { ApplicationErrorFallback } from "components/ApplicationErrorFallback";
import { Seo } from "components/base/Seo";
import { usePage } from "hooks";
import { useExperiment } from "hooks/useExperiments/useExperiments";
import { DynamicComponentList, dynamicComponentList } from "lib/dynamicComponentList";
import { fetchPublicFeatures, FeatureFlags } from "lib/featureFlags";
import { FeatureFlagContext } from "lib/featureFlags/FeatureFlagProvider";
import { PreviewFlagSpec, PreviewDataSpec } from "lib/previewData/previewData";
import { getGraphQLSdk, getPageData } from "lib/strapi";
import { Base, DisclosureOrderTitles, Disclosures } from "types";
import { PageContent, PublicationState } from "types/graphql/generated";

interface PageProps extends PageContent {
  base: Base;
  disclosures: Disclosures;
  disclosureOrderTitles: DisclosureOrderTitles;
  dynamicComponentList: DynamicComponentList;
  featureFlags: FeatureFlags;
  preview: boolean;
}

const StyledPage = styled.div`
  overflow-x: hidden;

  @supports (overflow: clip) {
    overflow-x: clip;
  }
`;

function Page({
  base,
  disclosures,
  featureFlags = {},
  pages: {
    data: [{ attributes }],
  },
  preview,
  statePlans,
}: PageProps) {
  const { attributes: baseContent } = base?.data ?? {};
  const { components: pageComponents, seo, experiments } = attributes;

  const { components, dynamicComponentsContent } = usePage({
    disclosures,
    pageComponents,
    dynamicComponentList,
    baseContent,
    statePlans,
  });

  const { isReady: canRenderComponents } = useExperiment(experiments?.experimentsList, experiments?.enabled);

  return (
    <FeatureFlagContext.Provider value={{ ...featureFlags }}>
      <StyledPage>
        {canRenderComponents && (
          <>
            {components.map(({ Component, name }, index) => {
              return <Component content={dynamicComponentsContent[index]} key={`${name}-${index}`} />;
            })}
            {seo && <Seo content={seo} />}
            <ToolTip />
            {preview && <Preview />}
          </>
        )}
      </StyledPage>
    </FeatureFlagContext.Provider>
  );
}

const getStaticPaths: GetStaticPaths = async () => {
  gql`
    query PageSlugs {
      pages {
        data {
          attributes {
            slug
            block_from_production
          }
        }
      }
    }
  `;

  // always first phase of build, don't cache
  const {
    pages: { data },
  } = await getGraphQLSdk(PublicationState.Preview).PageSlugs();

  const paths = data
    .filter(({ attributes: { block_from_production } }) => {
      return process.env.NEXT_PUBLIC_DEPLOYED_ENV !== "production" || !block_from_production;
    })
    .map(({ attributes: { slug } }: { attributes: { slug: string } }) => {
      return {
        params: {
          slug: slug ? slug.split("/") : undefined,
        },
      };
    });

  return {
    paths,
    fallback: false,
  };
};

const getStaticProps: GetStaticProps = async ({ params, preview = false, previewData = {} }) => {
  // eslint-disable-next-line no-nested-ternary
  const slug = params?.slug ? (Array.isArray(params.slug) ? params.slug.join("/") : params.slug) : null;

  gql`
    query PageContent($publicationState: PublicationState!, $slug: String) {
      pages(publicationState: $publicationState, filters: { slug: { eq: $slug } }) {
        data {
          attributes {
            components {
              ...DynamicContent
            }
            seo {
              ...SeoContent
            }
            experiments {
              ...ExperimentsContent
            }
          }
        }
      }
      statePlans(publicationState: $publicationState) {
        data {
          attributes {
            state
            stateInfo
          }
        }
      }
    }
  `;

  const publicationState = preview ? PublicationState.Preview : PublicationState.Live;

  const pageContentDocument = await getGraphQLSdk(publicationState).PageContent({
    publicationState,
    slug,
  });

  const pageData = await getPageData(pageContentDocument, publicationState);

  const featureFlags = (await fetchPublicFeatures()) as FeatureFlags;

  if (preview && previewData) {
    const previewFlagOverrides = (previewData as PreviewDataSpec).flags;

    Object.keys(previewFlagOverrides).forEach((key) => {
      const value = previewFlagOverrides[key];

      (featureFlags as PreviewFlagSpec)[key] = value;
    });
  }

  return {
    props: { ...pageData, featureFlags, key: slug, preview },
  };
};

export { getStaticPaths, getStaticProps };
export default withErrorBoundary(Page, { fallback: <ApplicationErrorFallback /> });
